/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Typography, Stack, Alert, CircularProgress,
    TextField, Autocomplete,
} from '@mui/material';
import Iconify from '../Iconify';
import { callApi } from '../../utils/apiCall';
import { useSelector } from '../../redux/store';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';

const BOOKING_STEPS = [
    { index: 1, label: 'Confirming appointment' },
    { index: 2, label: 'Updating patient record' },
    { index: 3, label: 'Finalising booking' },
];

function formatSlotLabel(isoString) {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch {
        return isoString;
    }
}

/**
 * PMS Booking Confirmation Wizard Step — embedded within the standard CRM follow-up flow.
 *
 * Active when pmsEnabledGlobal=true AND dentallyEnabledGlobal=false.
 *
 * Mirrors the visual style of DentallyFollowUpConfirmWizardStep.
 *
 * Props:
 *   practiceId          — required
 *   pmsSlot             — selected slot object from PmsAvailabilityWizardStep
 *   existingLead        — lead object (id, first_name, last_name, lead_contact)
 *   existingTreatmentId — integer treatment record ID (optional)
 *   onClose             — (success: boolean) => void
 *   onStepStateChange   — ({ buttonLabel, buttonIcon, buttonDisabled, buttonLoading }) => void
 */
const PmsBookingConfirmStep = forwardRef(({
    pmsSlot,
    practiceId,
    existingLead,
    existingTreatmentId,
    onClose,
    onStepStateChange,
}, ref) => {
    const allUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);

    const assignableUsers = allUsers
        .filter((u) => u?.is_enabled && u?.role !== 'ADMIN' && u?.role !== 'SUPPORT' && !u?.user?.is_superuser)
        .map((u) => ({
            id: u.id,
            name: `${u?.user?.first_name || ''} ${u?.user?.last_name || ''}`.trim(),
        }));

    const [notes, setNotes] = useState('');
    const [assignees, setAssignees] = useState([]);

    const [loading, setLoading] = useState(false);
    const [bookingStep, setBookingStep] = useState(0);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const practitionerName = pmsSlot?._practitioner_name || '';
    const canConfirm = pmsSlot && !loading && !success;

    // Report step state to parent so the footer can show the right button
    useEffect(() => {
        onStepStateChange?.({
            buttonLabel: 'Confirm Booking',
            buttonIcon: 'mdi:check-bold',
            buttonDisabled: !canConfirm,
            buttonLoading: loading,
            hideFooterOnSuccess: success,
        });
    }, [canConfirm, loading, success]);

    const handleConfirm = useCallback(async () => {
        if (!canConfirm) return;
        setError(null);
        setLoading(true);
        setBookingStep(1);

        try {
            const payload = {
                slot: {
                    start_time: pmsSlot.start_time,
                    end_time: pmsSlot.end_time || pmsSlot.finish_time,
                    practitioner_id: pmsSlot.practitioner_id,
                    schedule_id: pmsSlot._schedule_id,
                },
                notes,
                assignees: assignees.map((u) => u.id),
                ...(existingTreatmentId ? { treatment_id: existingTreatmentId } : {}),
                ...(existingLead?.id ? { lead_id: existingLead.id } : {}),
            };

            setBookingStep(2);
            const r = await callApi(`/api/v1/practices/${practiceId}/pms/book/`, 'POST', payload);

            setBookingStep(3);
            if (r.ok) {
                setSuccess(true);
                onClose?.(true);
            } else {
                const msg = r?.data?.detail || r?.data?.error || JSON.stringify(r?.data) || 'Booking failed. Please try again.';
                setError(msg);
            }
        } catch (err) {
            setError(`An unexpected error occurred: ${err?.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
            setBookingStep(0);
        }
    }, [canConfirm, pmsSlot, notes, assignees, existingTreatmentId, existingLead, practiceId, onClose]);

    useImperativeHandle(ref, () => ({
        submit() {
            if (!canConfirm) return null;
            handleConfirm();
            return null;
        },
    }), [canConfirm, handleConfirm]);

    // Success state
    if (success) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Box sx={{
                    width: 64, height: 64, borderRadius: '50%',
                    bgcolor: 'rgba(0,171,85,0.12)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                }}>
                    <Iconify icon="mdi:check-circle" width={36} sx={{ color: 'success.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Appointment Booked</Typography>
                <Typography variant="body2" color="text.secondary">
                    The appointment has been successfully booked in the PMS.
                </Typography>
            </Box>
        );
    }

    // Loading / progress state
    if (loading) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={44} sx={{ mb: 2, color: 'success.main' }} />
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Booking in progress…</Typography>
                <Box sx={{ textAlign: 'left', maxWidth: 260, mx: 'auto' }}>
                    {BOOKING_STEPS.map(({ index, label }) => {
                        let icon;
                        if (bookingStep > index) icon = <Iconify icon="mdi:check-circle" width={18} sx={{ color: 'success.main', flexShrink: 0 }} />;
                        else if (bookingStep === index) icon = <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: 'success.main' }} />;
                        else icon = <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />;
                        return (
                            <Stack key={index} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                                {icon}
                                <Typography variant="body2" sx={{
                                    color: bookingStep >= index ? 'text.primary' : 'text.disabled',
                                    fontWeight: bookingStep === index ? 600 : 400,
                                }}>
                                    {label}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    const patientName = [existingLead?.first_name, existingLead?.last_name].filter(Boolean).join(' ');

    return (
        <Box>
            {/* Patient summary */}
            {existingLead && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, mb: 2, borderRadius: 2,
                    bgcolor: 'rgba(7,141,238,0.04)',
                    border: '1px solid rgba(7,141,238,0.16)',
                }}>
                    <Iconify icon="mdi:account-circle" width={22} sx={{ color: '#078DEE', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{patientName}</Typography>
                        {existingLead?.lead_contact?.email && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {existingLead.lead_contact.email}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* Slot summary */}
            {pmsSlot && (
                <Box sx={{
                    p: 1.5, bgcolor: 'rgba(0,171,85,0.08)',
                    borderRadius: 2.5, mb: 2,
                    border: '1px solid rgba(0,171,85,0.3)',
                }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon="mdi:calendar-check" width={18} sx={{ color: 'success.main' }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                {formatSlotLabel(pmsSlot.start_time)}
                            </Typography>
                            {practitionerName && (
                                <Typography variant="caption" color="text.secondary">
                                    {practitionerName}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            )}

            {/* Appointment details */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:clipboard-text-outline" width={15} sx={{ color: 'primary.main' }} />
                    Appointment Details
                </Typography>

                {/* Assignees */}
                <Autocomplete
                    multiple
                    size="small"
                    sx={{ mb: 2 }}
                    options={assignableUsers}
                    getOptionLabel={(u) => u.name}
                    value={assignees}
                    onChange={(_, val) => setAssignees(val)}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Assignees"
                            placeholder="Search users…"
                        />
                    )}
                />

                {/* Notes */}
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>
    );
});

export default PmsBookingConfirmStep;
